import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { getOptimizedImageUrl, generateResponsiveSrcSet } from '@/utils/imageOptimization';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import RevealBlock from '@/components/common/RevealBlock';
import { useNoticiasQuery } from '@/hooks/useNoticiasQuery';

const NewsCarousel = () => {
  const { noticias: allNoticias, isLoading } = useNoticiasQuery();
  const noticias = useMemo(() => allNoticias.slice(0, 5), [allNoticias]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { ref: sectionRef, visible: sectionVisible } = useScrollReveal(0.3);

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % noticias.length);
  }, [noticias.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + noticias.length) % noticias.length);
  }, [noticias.length]);

  useEffect(() => {
    if (noticias.length === 0 || !sectionVisible) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [noticias.length, currentIndex, sectionVisible, nextSlide]);

  if (isLoading) {
    return (
      <div ref={sectionRef} className="py-20 md:py-28 bg-black">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-white/5 rounded w-64 mx-auto mb-8" />
            <div className="h-96 bg-white/5 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (noticias.length === 0) return null;

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden">
      {/* Orb */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="sobre-orb sobre-orb-1" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <RevealBlock>
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.25em] uppercase text-white/40 mb-4 font-medium">Fique por dentro</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              <span className="text-white">Notícias do </span><span className="sobre-gradient-text">Bailão</span>
            </h2>
            <p className="text-lg text-white/50 max-w-3xl mx-auto">Fique por dentro das últimas novidades do mundo da música.</p>
          </div>
        </RevealBlock>

        <RevealBlock delay={200}>
          <div className="relative max-w-6xl mx-auto">
            <div className="overflow-hidden rounded-2xl sobre-glass-card !p-0 !rounded-2xl">
              <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                {noticias.map(noticia => (
                  <div key={noticia.id} className="w-full flex-shrink-0">
                    <div className="relative group cursor-pointer">
                      <div className="relative h-[500px] md:h-[420px] overflow-hidden">
                        <div className="absolute inset-0">
                          <img
                            src={getOptimizedImageUrl(noticia.imagem_url, { width: 1200, height: 500, quality: 70 })}
                            srcSet={generateResponsiveSrcSet(noticia.imagem_url, [600, 900, 1200])}
                            sizes="(max-width: 768px) 100vw, 1200px"
                            alt={noticia.titulo}
                            className="w-full h-full object-cover"
                            loading="lazy" decoding="async" width={1200} height={500}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/70" />
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 p-6 md:absolute md:inset-0 md:flex md:flex-col md:items-center md:justify-center md:text-center md:p-8">
                          <Link to={`/noticia/${noticia.slug}`}>
                            <h3 className="text-xl md:text-3xl font-bold text-white mb-2 md:mb-4 line-clamp-2 hover:scale-105 transition-transform duration-300 cursor-pointer" style={{ textShadow: '0 2px 16px rgba(0,0,0,0.9)' }}>
                              {noticia.titulo}
                            </h3>
                          </Link>
                          <p className="text-white/70 mb-3 md:mb-6 line-clamp-2 text-xs md:text-base md:max-w-2xl" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                            {noticia.subtitulo}
                          </p>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:flex-col md:gap-4">
                            <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-white/60">
                              <span className="line-clamp-1">{noticia.autor}</span>
                              <span className="w-1 h-1 rounded-full bg-white/30" />
                              <time>{format(new Date(noticia.created_at), "dd 'de' MMMM", { locale: ptBR })}</time>
                            </div>
                            <Link
                              to={`/noticia/${noticia.slug}`}
                              className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-full sobre-cta-button text-white text-sm md:text-base font-medium hover:opacity-90 transition-all duration-300 hover:scale-[1.02]"
                            >
                              Saiba mais
                              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {noticias.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full sobre-glass-card !p-0 flex items-center justify-center text-white hover:scale-110 transition-transform z-10"
                  aria-label="Notícia anterior"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full sobre-glass-card !p-0 flex items-center justify-center text-white hover:scale-110 transition-transform z-10"
                  aria-label="Próxima notícia"
                >
                  <ChevronRight size={24} />
                </button>

                <div className="flex justify-center gap-2 mt-6">
                  {noticias.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className="transition-all duration-300"
                      aria-label={`Ir para notícia ${index + 1}`}
                      aria-current={index === currentIndex ? 'true' : 'false'}
                    >
                      <span className={`block rounded-full transition-all duration-300 ${
                        index === currentIndex
                          ? 'w-8 h-2 sobre-cta-button'
                          : 'w-2 h-2 bg-white/15 hover:bg-white/25'
                      }`} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </RevealBlock>
      </div>
    </section>
  );
};

export default NewsCarousel;